// keyrd_console/src/components/PilotPreflight.js
import React, { useEffect, useMemo, useState } from "react";
import {
  getPilotPreflightDetail,
  getPilotPreflightList,
} from "../api/pilotAdmin";
import PilotGovernanceBanner from "./PilotGovernanceBanner";
import PilotStatusPill from "./PilotStatusPill";
import PilotDataSourceBadge from "./PilotDataSourceBadge";
import PilotReviewActionPanel from "./PilotReviewActionPanel";

const pageStyle = {
  padding: 20,
  display: "flex",
  flexDirection: "column",
  gap: 16,
};

const titleRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
};

const titleStyle = {
  margin: 0,
  fontSize: 24,
  fontWeight: 700,
  color: "#0f172a",
};

const subtitleStyle = {
  color: "#475569",
  fontSize: 14,
  marginTop: 4,
};

const gridStyle = {
  display: "grid",
  gridTemplateColumns: "minmax(320px, 420px) minmax(420px, 1fr)",
  gap: 16,
};

const columnStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 12,
};

const panelStyle = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 12,
  padding: 16,
};

const panelTitleStyle = {
  margin: "0 0 12px 0",
  fontSize: 16,
  fontWeight: 700,
  color: "#0f172a",
};

const listStyle = {
  display: "flex",
  flexDirection: "column",
  gap: 10,
};

const rowStyle = (selected) => ({
  border: selected ? "1px solid #86efac" : "1px solid #e2e8f0",
  background: selected ? "#f0fdf4" : "#f8fafc",
  borderRadius: 10,
  padding: 12,
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  gap: 8,
});

const metaTextStyle = {
  fontSize: 13,
  color: "#64748b",
};

const gateRowStyle = {
  display: "grid",
  gridTemplateColumns: "1fr auto",
  gap: 10,
  alignItems: "center",
  padding: "10px 0",
  borderBottom: "1px solid #e2e8f0",
};

const kvRowStyle = {
  display: "grid",
  gridTemplateColumns: "180px 1fr",
  gap: 10,
  alignItems: "start",
  fontSize: 14,
};

const keyStyle = {
  color: "#475569",
  fontWeight: 600,
};

const valueStyle = {
  color: "#0f172a",
};

const warningStyle = {
  padding: 12,
  borderRadius: 10,
  background: "#fff7ed",
  color: "#9a3412",
  border: "1px solid #fdba74",
  fontSize: 13,
  fontWeight: 600,
};

const loadingStyle = {
  padding: 18,
  borderRadius: 10,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  color: "#334155",
};

const errorStyle = {
  padding: 14,
  borderRadius: 10,
  background: "#fef2f2",
  color: "#991b1b",
  border: "1px solid #fecaca",
};

const emptyStyle = {
  padding: 18,
  borderRadius: 10,
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  color: "#475569",
};

function KV({ label, value }) {
  return (
    <div style={kvRowStyle}>
      <div style={keyStyle}>{label}</div>
      <div style={valueStyle}>{value || "—"}</div>
    </div>
  );
}

function normalizeList(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.participants)) return payload.participants;
  if (Array.isArray(payload?.rows)) return payload.rows;
  return [];
}

function normalizeDetail(payload) {
  return payload?.data || payload || {};
}

function readinessSummary(detail = {}) {
  const gates = Array.isArray(detail.readiness_gates)
    ? detail.readiness_gates
    : [];
  const missing = Array.isArray(detail.missing_dependencies)
    ? detail.missing_dependencies
    : [];
  return { gates, missing };
}

function PilotPreflight({ auth, participantId, onSelectParticipant }) {
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");
  const [listPayload, setListPayload] = useState(null);

  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [detailPayload, setDetailPayload] = useState(null);

  const rows = useMemo(() => normalizeList(listPayload?.data || listPayload), [listPayload]);
  const activeParticipantId =
    participantId || rows?.[0]?.participant_uuid || null;

  useEffect(() => {
    let cancelled = false;

    async function loadList() {
      setListLoading(true);
      setListError("");

      try {
        const res = await getPilotPreflightList(auth);
        if (!cancelled) {
          setListPayload(res);
        }
      } catch (err) {
        if (!cancelled) {
          setListError(err?.message || "Failed to load preflight list.");
        }
      } finally {
        if (!cancelled) {
          setListLoading(false);
        }
      }
    }

    loadList();
    return () => {
      cancelled = true;
    };
  }, [auth]);

  useEffect(() => {
    let cancelled = false;

    async function loadDetail() {
      if (!activeParticipantId) {
        setDetailPayload(null);
        setDetailError("");
        return;
      }

      setDetailLoading(true);
      setDetailError("");

      try {
        const res = await getPilotPreflightDetail(activeParticipantId, auth);
        if (!cancelled) {
          setDetailPayload(res);
        }
      } catch (err) {
        if (!cancelled) {
          setDetailError(err?.message || "Failed to load preflight detail.");
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false);
        }
      }
    }

    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [activeParticipantId, auth]);

  const detail = normalizeDetail(detailPayload);
  const dataSource =
    detail.data_source ||
    listPayload?.data?.data_source ||
    listPayload?.data_source ||
    "unknown";
  const stub =
    Boolean(detail.stub) ||
    Boolean(listPayload?.data?.stub) ||
    Boolean(listPayload?.stub);

  const { gates, missing } = readinessSummary(detail);

  return (
    <div style={pageStyle}>
      <div style={titleRowStyle}>
        <div>
          <h1 style={titleStyle}>Preflight</h1>
          <div style={subtitleStyle}>
            Cohort-level readiness review with explicit, audit-aware decisions and
            no activation path.
          </div>
        </div>
        <PilotDataSourceBadge source={dataSource} stub={stub} />
      </div>

      <PilotGovernanceBanner auth={auth} system={detail.system || detail} />

      <div style={warningStyle}>
        Preflight is review-only. Outcomes here must never activate live
        intervention or mutate RL state.
      </div>

      <div style={gridStyle}>
        <div style={columnStyle}>
          <div style={panelStyle}>
            <h2 style={panelTitleStyle}>Review Queue</h2>

            {listLoading ? (
              <div style={loadingStyle}>Loading preflight queue…</div>
            ) : listError ? (
              <div style={errorStyle}>{listError}</div>
            ) : !rows.length ? (
              <div style={emptyStyle}>No participants available for preflight review.</div>
            ) : (
              <div style={listStyle}>
                {rows.map((row) => {
                  const selected = row.participant_uuid === activeParticipantId;
                  return (
                    <div
                      key={row.participant_uuid}
                      style={rowStyle(selected)}
                      onClick={() =>
                        typeof onSelectParticipant === "function" &&
                        onSelectParticipant(row.participant_uuid)
                      }
                    >
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>
                        {row.participant_label || "Unnamed Participant"}
                      </div>
                      <div style={metaTextStyle}>{row.participant_uuid}</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                        <PilotStatusPill
                          status={row.lifecycle_state || "unknown"}
                        />
                        <PilotStatusPill
                          status={row.operator_review_status || "review_pending"}
                        />
                      </div>
                      <div style={metaTextStyle}>
                        Missing:{" "}
                        {Array.isArray(row.missing_dependencies) &&
                        row.missing_dependencies.length
                          ? row.missing_dependencies.join(", ")
                          : "none"}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <div style={columnStyle}>
          {detailLoading ? (
            <div style={loadingStyle}>Loading preflight detail…</div>
          ) : detailError ? (
            <div style={errorStyle}>{detailError}</div>
          ) : !activeParticipantId ? (
            <div style={emptyStyle}>No participant selected for preflight.</div>
          ) : (
            <>
              <div style={panelStyle}>
                <h2 style={panelTitleStyle}>Participant Readiness</h2>
                <KV
                  label="Participant"
                  value={detail.participant_label || detail.identity?.participant_label}
                />
                <KV
                  label="Participant UUID"
                  value={detail.participant_uuid || detail.identity?.participant_uuid}
                />
                <KV
                  label="Lifecycle State"
                  value={detail.lifecycle_state || detail.operational_state?.lifecycle_state}
                />
                <KV
                  label="Review Status"
                  value={
                    detail.operator_review_status ||
                    detail.operational_state?.operator_review_status
                  }
                />
                <KV
                  label="Missing Dependencies"
                  value={missing.length ? missing.join(", ") : "None"}
                />
              </div>

              <div style={panelStyle}>
                <h2 style={panelTitleStyle}>Readiness Gates</h2>
                {!gates.length ? (
                  <div style={valueStyle}>No readiness gates available.</div>
                ) : (
                  gates.map((gate, idx) => (
                    <div key={`${gate.gate_name || "gate"}-${idx}`} style={gateRowStyle}>
                      <div>
                        <div style={{ fontWeight: 700, color: "#0f172a" }}>
                          {gate.gate_name || "Unnamed gate"}
                        </div>
                        <div style={metaTextStyle}>
                          {gate.description || "No gate description provided."}
                        </div>
                      </div>
                      <PilotStatusPill
                        status={gate.status || "unknown"}
                        label={gate.status || "unknown"}
                      />
                    </div>
                  ))
                )}
              </div>

              <PilotReviewActionPanel
                auth={auth}
                participantId={activeParticipantId}
                initialEvidenceState={
                  detail.evidence_state || detail.operational_state?.evidence_state || ""
                }
                initialEvidenceConfidence={
                  detail.evidence_confidence ||
                  detail.operational_state?.evidence_confidence ||
                  ""
                }
                onReviewSubmitted={() => {
                  // Let the next page load refresh naturally via route/tab revisit or future reload hooks.
                }}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default PilotPreflight;